<template>
  <ErrorModal />
  <div class="q-pa-md visualping-data-page">
    <div class="row q-col-gutter-md q-mb-md">
      <div class="col-12 col-md-3">
        <div class="filter-box full-height q-pa-md">
          <span class="filter-box-heading">Search</span>
          <q-input
            class="q-mt-sm"
            v-model="filters.searchTerm"
            outlined
            debounce="500"
            placeholder="Search"
            bg-color="white"
          >
            <template v-slot:append>
              <q-icon name="search" />
            </template>
          </q-input>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="filter-box full-height q-pa-md">
          <span class="filter-box-heading">Date range</span>
          <div class="row q-col-gutter-sm q-mt-sm">
            <div class="col-12">
              <q-input
                v-model="filters.startDate"
                outlined
                type="date"
                label="Start date"
                bg-color="white"
                clearable
              />
            </div>
            <div class="col-12">
              <q-input
                v-model="filters.endDate"
                outlined
                type="date"
                label="End date"
                bg-color="white"
                clearable
              />
            </div>
            <div class="col-12">
              <div class="row q-col-gutter-sm">
                <div class="col-6">
                  <q-btn
                    dense
                    no-caps
                    outline
                    color="indigo-10"
                    label="Today"
                    class="full-width"
                    @click="setTodayDateRange"
                  />
                </div>
                <div class="col-6">
                  <q-btn
                    dense
                    no-caps
                    outline
                    color="indigo-10"
                    label="Clear"
                    class="full-width"
                    :disable="!filters.startDate && !filters.endDate"
                    @click="clearDateRange"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="filter-box statuses-checkbox full-height q-pa-md">
          <span class="filter-box-heading">Notification filters</span>
          <q-option-group
            v-model="filters.statusGroup"
            :options="statusOptions"
            type="checkbox"
          />
        </div>
      </div>
      <div class="col-12 col-md-3">
        <div class="filter-box statuses-checkbox full-height q-pa-md">
          <span class="filter-box-heading">Scheme types</span>
          <q-option-group
            v-model="filters.schemeGroup"
            :options="schemeTypeOptions"
            type="checkbox"
          />
        </div>
      </div>
    </div>

    <q-table
      class="sticky-header"
      table-header-style="background-color: #0a1f8f"
      table-header-class="text-white"
      title="Visualping data"
      :rows="rows"
      :columns="columns"
      row-key="id"
      :loading="loading"
      :dense="$q.screen.lt.md"
      v-model:pagination="pagination"
      @request="onRequest"
      binary-state-sort
      :rows-per-page-options="[25, 50, 100]"
    >
      <template v-slot:loading>
        <q-inner-loading showing color="primary"></q-inner-loading>
      </template>

      <template v-slot:top>
        <div class="row items-center full-width q-col-gutter-sm">
          <div class="col">
            <div class="text-h6">Visualping data</div>
          </div>
          <div class="col-auto">
            <q-btn
              dense
              no-caps
              outline
              color="indigo-10"
              label="Select all visible"
              @click="selectAllVisible"
              :disable="loading || rows.length === 0"
            />
          </div>
          <div class="col-auto">
            <q-btn
              dense
              no-caps
              outline
              color="indigo-10"
              label="Select none"
              @click="clearSelection"
              :disable="selectedNotificationIds.length === 0"
            />
          </div>
          <div class="col-auto">
            <q-btn
              dense
              no-caps
              color="green"
              label="Set selected checked"
              @click="setSelectedCheckedState(true)"
              :disable="loading || selectedNotificationIds.length === 0"
            >
              <i class="las la-check-circle la-lg q-ml-sm"></i>
            </q-btn>
          </div>
          <div class="col-auto">
            <q-btn
              dense
              no-caps
              color="grey-8"
              label="Set selected unchecked"
              @click="setSelectedCheckedState(false)"
              :disable="loading || selectedNotificationIds.length === 0"
            >
              <i class="las la-square la-lg q-ml-sm"></i>
            </q-btn>
          </div>
          <div class="col-auto">
            <q-btn
              dense
              no-caps
              class="orange-btn-filled bold-font"
              label="Export visible"
              icon-right="las la-file-download"
              @click="exportToExcel"
              :disable="loading || rows.length === 0"
            />
          </div>
        </div>
      </template>

      <template v-slot:body="props">
        <q-tr :props="props">
          <q-td key="select" :props="props" auto-width>
            <q-checkbox
              :model-value="isSelected(props.row.id)"
              @update:model-value="value => updateSelection(props.row.id, value)"
            />
          </q-td>
          <q-td key="schemeName" :props="props">
            <span
              class="cursor-pointer text-primary"
              @click="goToSchemeEdit(props.row.schemeId)"
            >
              {{ props.row.schemeName || "-" }}
            </span>
            <q-tooltip
              v-if="props.row.schemeName"
              anchor="top middle"
              self="bottom middle"
            >
              {{ props.row.schemeName }}
            </q-tooltip>
          </q-td>
          <q-td key="schemeIdentifier" :props="props">
            <span
              class="cursor-pointer text-primary"
              @click="goToSchemeEdit(props.row.schemeId)"
            >
              {{ props.row.schemeIdentifier || "-" }}
            </span>
          </q-td>
          <q-td key="summarizer" :props="props" class="ellipsis-cell">
            <span>{{ props.row.summarizer || "-" }}</span>
            <q-tooltip
              v-if="props.row.summarizer"
              anchor="top middle"
              self="bottom middle"
            >
              {{ props.row.summarizer }}
            </q-tooltip>
          </q-td>
          <q-td key="datetime" :props="props">
            {{ formatDate(props.row.datetime) }}
          </q-td>
          <q-td key="url" :props="props" class="ellipsis-cell">
            <a
              v-if="props.row.url"
              :href="props.row.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary"
            >
              {{ props.row.url }}
            </a>
            <span v-else>-</span>
            <q-tooltip v-if="props.row.url" anchor="top middle" self="bottom middle">
              {{ props.row.url }}
            </q-tooltip>
          </q-td>
          <q-td key="visualPing" :props="props" auto-width>
            <q-btn
              v-if="props.row.jobId"
              :href="`https://visualping.io/jobs/${props.row.jobId}`"
              target="_blank"
              type="a"
              dense
              size="12px"
              round
              flat
              color="indigo-10"
              icon="las la-external-link-alt"
              style="text-decoration: none"
            >
              <q-tooltip>Open in VisualPing</q-tooltip>
            </q-btn>
            <span v-else>-</span>
          </q-td>
          <q-td key="isChecked" :props="props" auto-width>
            <q-btn
              dense
              round
              flat
              :color="props.row.isChecked ? 'green' : 'grey-8'"
              :icon="props.row.isChecked ? 'check_box' : 'check_box_outline_blank'"
              @click="toggleCheckedState(props.row)"
            >
              <q-tooltip>
                {{ props.row.isChecked ? "Set unchecked" : "Set checked" }}
              </q-tooltip>
            </q-btn>
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";
import store from "../../store/index";
import ErrorModal from "./ErrorModal.vue";
import LinkcheckerService from "../../services/linkchecker.service";

const columns = [
  {
    name: "select",
    label: "",
    align: "left",
    field: row => row.id
  },
  {
    name: "schemeName",
    label: "Scheme",
    align: "left",
    field: row => row.schemeName,
    sortable: true,
    style: "max-width: 14vw",
    classes: "ellipsis",
    headerStyle: "max-width: 14vw",
    headerClasses: "ellipsis"
  },
  {
    name: "schemeIdentifier",
    label: "Scheme number",
    align: "left",
    field: row => row.schemeIdentifier,
    sortable: true
  },
  {
    name: "summarizer",
    label: "Summary",
    align: "left",
    field: row => row.summarizer,
    sortable: true,
    style: "max-width: 22vw",
    classes: "ellipsis",
    headerStyle: "max-width: 22vw",
    headerClasses: "ellipsis"
  },
  {
    name: "datetime",
    label: "Date time",
    align: "left",
    field: row => row.datetime,
    sortable: true
  },
  {
    name: "url",
    label: "Url",
    align: "left",
    field: row => row.url,
    sortable: true,
    style: "max-width: 24vw",
    classes: "ellipsis",
    headerStyle: "max-width: 24vw",
    headerClasses: "ellipsis"
  },
  {
    name: "visualPing",
    label: "VisualPing",
    align: "center",
    field: row => row.jobId,
    sortable: false
  },
  {
    name: "isChecked",
    label: "Checked",
    align: "center",
    field: row => row.isChecked,
    sortable: true
  }
];

const statusOptions = [
  { label: "Show broken", value: "broken" },
  { label: "Show changed", value: "changed" },
  { label: "Show undetermined", value: "undetermined" },
  { label: "Show checked", value: "checked" }
];

const schemeTypeOptions = [
  { label: "Show German schemes", value: "german" },
  { label: "Show Dutch schemes", value: "dutch" },
  { label: "Show European schemes", value: "european" }
];

export default {
  name: "VisualPingData",
  components: {
    ErrorModal
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const selectedNotificationIds = ref([]);
    const skipDateRangeWatcher = ref(false);

    const rows = computed({
      get: () => store.state.linkChecker.visualPingData
    });

    const pagination = ref(
      computed({
        get: () => store.state.linkChecker.visualPingDataPagination,
        set: paginationVar =>
          store.commit("linkChecker/setVisualPingDataPagination", paginationVar)
      })
    );

    const filters = reactive({
      searchTerm: "",
      startDate: null,
      endDate: null,
      statusGroup: ["broken", "changed", "undetermined"],
      schemeGroup: ["german", "dutch", "european"]
    });

    const buildRequestParams = paginationValue => ({
      filter: filters.searchTerm,
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      showBroken: filters.statusGroup.includes("broken"),
      showChanged: filters.statusGroup.includes("changed"),
      showUndetermined: filters.statusGroup.includes("undetermined"),
      showChecked: filters.statusGroup.includes("checked"),
      showGermanSchemes: filters.schemeGroup.includes("german"),
      showDutchSchemes: filters.schemeGroup.includes("dutch"),
      showEuropeanSchemes: filters.schemeGroup.includes("european"),
      sortBy: paginationValue.sortBy,
      descending: paginationValue.descending,
      index: paginationValue.page ? paginationValue.page - 1 : 0,
      size:
        paginationValue.rowsPerPage === 0
          ? pagination.value.rowsNumber
          : paginationValue.rowsPerPage ?? 50
    });

    const loadVisualPingData = async requestedPagination => {
      loading.value = true;

      try {
        await LinkcheckerService.getVisualPingData(
          buildRequestParams(requestedPagination)
        );
      } finally {
        loading.value = false;
      }
    };

    const refreshVisualPingData = async () => {
      store.commit("linkChecker/updateVisualPingDataPaginationPage", {
        ...pagination.value,
        page: 1
      });

      await loadVisualPingData(pagination.value);
      clearSelection();
    };

    const onRequest = async props => {
      const { page, rowsPerPage, sortBy, descending } = props.pagination;
      const fetchCount =
        rowsPerPage === 0 ? pagination.value.rowsNumber : rowsPerPage;

      loading.value = true;

      try {
        await LinkcheckerService.getVisualPingData(
          buildRequestParams({
            ...props.pagination,
            rowsPerPage: fetchCount,
            page,
            sortBy,
            descending
          })
        );
        store.commit("linkChecker/updateVisualPingDataPaginationPage", props.pagination);
        clearSelection();
      } finally {
        loading.value = false;
      }
    };

    const isSelected = notificationId =>
      selectedNotificationIds.value.includes(notificationId);

    const updateSelection = (notificationId, isSelectedRow) => {
      if (isSelectedRow) {
        if (!selectedNotificationIds.value.includes(notificationId))
          selectedNotificationIds.value.push(notificationId);
        return;
      }

      selectedNotificationIds.value = selectedNotificationIds.value.filter(
        id => id !== notificationId
      );
    };

    const selectAllVisible = () => {
      selectedNotificationIds.value = rows.value.map(row => row.id);
    };

    const clearSelection = () => {
      selectedNotificationIds.value = [];
    };

    const setSelectedCheckedState = async isChecked => {
      if (selectedNotificationIds.value.length === 0) return;

      loading.value = true;
      try {
        await LinkcheckerService.setVisualPingNotificationsCheckedState(
          selectedNotificationIds.value,
          isChecked
        );
        await loadVisualPingData(pagination.value);
        clearSelection();
      } finally {
        loading.value = false;
      }
    };

    const toggleCheckedState = async row => {
      loading.value = true;
      try {
        await LinkcheckerService.setVisualPingNotificationsCheckedState(
          [row.id],
          !row.isChecked
        );
        await loadVisualPingData(pagination.value);
        clearSelection();
      } finally {
        loading.value = false;
      }
    };

    const exportToExcel = async () => {
      loading.value = true;
      try {
        const response = await LinkcheckerService.exportVisualPingData(
          buildRequestParams(pagination.value)
        );

        const fileURL = window.URL.createObjectURL(
          new Blob([response.data], { type: response.data.type })
        );
        const a = document.createElement("a");
        a.setAttribute("style", "display:none;");
        document.body.appendChild(a);
        a.download = "VisualPingData.xlsx";
        a.href = fileURL;
        a.target = "_blank";
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(fileURL);
      } finally {
        loading.value = false;
      }
    };

    const goToSchemeEdit = schemeId => {
      if (!schemeId) return;
      router.push({ name: "EditScheme", params: { id: schemeId } });
    };

    const formatDate = value => {
      if (!value) return "-";
      return moment(value).format("DD-MM-YYYY HH:mm");
    };

    const setTodayDateRange = async () => {
      const today = moment().format("YYYY-MM-DD");
      skipDateRangeWatcher.value = true;
      filters.startDate = today;
      filters.endDate = today;
      await nextTick();
      skipDateRangeWatcher.value = false;
      await refreshVisualPingData();
    };

    const clearDateRange = async () => {
      skipDateRangeWatcher.value = true;
      filters.startDate = null;
      filters.endDate = null;
      await nextTick();
      skipDateRangeWatcher.value = false;
      await refreshVisualPingData();
    };

    watch(
      () => filters.searchTerm,
      async () => {
        await refreshVisualPingData();
      }
    );

    watch(
      () => filters.statusGroup.join("|"),
      async () => {
        await refreshVisualPingData();
      }
    );

    watch(
      () => filters.schemeGroup.join("|"),
      async () => {
        await refreshVisualPingData();
      }
    );

    watch(
      () => [filters.startDate, filters.endDate],
      async () => {
        if (skipDateRangeWatcher.value) return;
        await refreshVisualPingData();
      }
    );


    onMounted(async () => {
      await loadVisualPingData(pagination.value);
    });

    return {
      columns,
      filters,
      loading,
      rows,
      pagination,
      selectedNotificationIds,
      statusOptions,
      schemeTypeOptions,
      onRequest,
      isSelected,
      updateSelection,
      selectAllVisible,
      clearSelection,
      setSelectedCheckedState,
      toggleCheckedState,
      exportToExcel,
      goToSchemeEdit,
      formatDate,
      setTodayDateRange,
      clearDateRange
    };
  }
};
</script>

<style scoped>
.ellipsis-cell {
  max-width: 24vw;
}

.ellipsis-cell > span,
.ellipsis-cell > a {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.full-height {
  height: 100%;
}
</style>